
import React from "react";
import cookie from "react-cookies";

import Axios from "services/Axios";
import { KarunaCLOUD_responseValidator, KarunaCLOUD_slackDiagnosticsPrettifier } from "services/KarunaCloudServices";
import { grantAccountAccess, isLoggedIn, revokeAccountAccess } from "services/AccountServices";
import { mcpClientService } from "mcp/services/MCPClientService";
import { sessionManager } from "utility/SessionManager";
import { unhashAccessPath } from "services/AccessPathUtils";
import { NotificationManagerAPI, useNotificationManager } from "notifications/NotificationManager";

export type OnSuccessEmpty = () => void;
export type OnSuccessMessage = (message: string) => void;
export type OnSuccessAny = (response: any) => void;
export type OnFailEmpty = () => void;
export type OnFailError = (error: string) => void;
export type OnFailAny = (error: any) => void;
export type OnFinallyEmpty = () => void;
export type OnFinally = () => void;
export type OnLogout = () => void;
export type LogoutHook = (accessPath: string, reason: string, onSuccess: OnSuccessEmpty | null, onFail: OnFailEmpty | null) => Promise<void>;

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface ApiCallResult {
    wasCallable: boolean;
    wasSuccessful: boolean;
    validResponse: boolean;
    response?: any;
    error?: any;
}

export const createDefaultApiCallResult = (overrides: Partial<ApiCallResult> = {}): ApiCallResult => ({
    wasCallable: false,
    wasSuccessful: false,
    validResponse: false,
    response: undefined,
    error: undefined,
    ...overrides
});

export interface ApiHook {
    isLoggingIn: boolean;
    isLoggingOut: boolean;
    setIsLoggingIn: (value: boolean) => void;
    setIsLoggingOut: (value: boolean) => void;
    api_Get: (
        accessPath: string,
        url: string,
        notificationManager?: NotificationManagerAPI | null,
        onSuccess?: OnSuccessAny | null,
        onFail?: OnFailAny | null,
        onFinally?: OnFinally | null
    ) => Promise<ApiCallResult>;
    api_Post: (
        accessPath: string,
        url: string,
        data: any,
        notificationManager?: NotificationManagerAPI | null,
        onSuccess?: OnSuccessAny | null,
        onFail?: OnFailAny | null,
        onFinally?: OnFinally | null
    ) => Promise<ApiCallResult>;
    api_Patch: (
        accessPath: string,
        url: string,
        data: any,
        notificationManager?: NotificationManagerAPI | null,
        onSuccess?: OnSuccessAny | null,
        onFail?: OnFailAny | null,
        onFinally?: OnFinally | null
    ) => Promise<ApiCallResult>;
    api_Put: (
        accessPath: string,
        url: string,
        data: any,
        notificationManager?: NotificationManagerAPI | null,
        onSuccess?: OnSuccessAny | null,
        onFail?: OnFailAny | null,
        onFinally?: OnFinally | null
    ) => Promise<ApiCallResult>;
    api_Delete: (
        accessPath: string,
        url: string,
        notificationManager?: NotificationManagerAPI | null,
        onSuccess?: OnSuccessAny | null,
        onFail?: OnFailAny | null,
        onFinally?: OnFinally | null
    ) => Promise<ApiCallResult>;
    api_Diagnostics: (
        accessPath: string,
        error: any,
        onSuccess?: OnSuccessEmpty | null,
        onFail?: OnFailError | null,
        onFinally?: OnFinallyEmpty | null
    ) => Promise<ApiCallResult>;
    api_PatientAlerts: (
        accessPath: string,
        notificationManager: NotificationManagerAPI,
        onSuccess?: OnSuccessAny | null,
        onFail?: OnFailError | null
    ) => Promise<ApiCallResult>;
    api_PatientUsageStats: (
        accessPath: string,
        params: string,
        notificationManager: NotificationManagerAPI,
        onSuccess?: OnSuccessEmpty | null,
        onFail?: OnFailError | null
    ) => Promise<ApiCallResult>;
    api_Version: (
        accessPath: string,
        onSuccess?: OnSuccessMessage | null,
        onFail?: OnFailError | null
    ) => Promise<ApiCallResult>;
    api_Login: (
        accessPath: string,
        email: string,
        password: string,
        onSuccess: OnSuccessMessage | null,
        onFail: OnFailError | null,
        logDiagnostics?: boolean
    ) => Promise<void>;
    api_Logout: (
        accessPath: string,
        logoutReason: string,
        logoutHook?: LogoutHook | null,
        onLogout?: OnLogout | null,
        routeToLogin?: boolean,
        logDiagnostics?: boolean
    ) => Promise<void>;
    logout: (accessPath: string, onLogout?: OnLogout | null, routeToLogin?: boolean) => void;
}

export const useAPI = (): ApiHook => {
    const defaultNotificationManager = useNotificationManager();

    const [isLoggingIn, setIsLoggingIn] = React.useState(false);
    const [isLoggingOut, setIsLoggingOut] = React.useState(false);

    const apiBase = async (
        method: HttpMethod,
        accessPath: string,
        url: string,
        data: any = null,
        notificationManager: NotificationManagerAPI | null = null,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailAny | null = null,
        onFinally: OnFinally | null = null
    ): Promise<ApiCallResult> => {
        const result: ApiCallResult = {
            wasCallable: false,
            wasSuccessful: false,
            validResponse: false
        };

        // Use provided notification manager or default to the hook one
        const effectiveNotificationManager = notificationManager || defaultNotificationManager;

        try {
            if (!sessionManager.canMakeApiCall(url)) {
                console.warn(`${accessPath} X BLOCKED: ${method.toLowerCase()} ${url} (Session not active)`);
                result.wasCallable = false;
                return result;
            }

            result.wasCallable = true;

            let response: any;
            const config = { accessPath: accessPath };

            switch (method) {
                case "GET":
                    response = await Axios.get(url, config);
                    break;
                case "POST":
                    response = await Axios.post(url, data, config);
                    break;
                case "PATCH":
                    response = await Axios.patch(url, data, config);
                    break;
                case "PUT":
                    response = await Axios.put(url, data, config);
                    break;
                case "DELETE":
                    response = await Axios.delete(url, config);
                    break;
                default:
                    throw new Error(`Unsupported HTTP method: ${method}`);
            }

            result.response = response;

            // Use the hook-based validator with automatic notifications
            result.validResponse = KarunaCLOUD_responseValidator(response, effectiveNotificationManager);
            if (result.validResponse) {
                onSuccess?.(response);
            }

            result.wasSuccessful = result.validResponse;

            return result;
        }
        catch (error) {
            result.error = error;
            result.wasSuccessful = false;
            onFail?.(error);
            return result;
        }
        finally {
            onFinally?.();
        }
    };

    const logout = (accessPath: string, onLogout: OnLogout | null = null, routeToLogin: boolean = false): void => {
        void api_Post(accessPath, "/logout/", null, null, null, null, () => {
            revokeAccountAccess(routeToLogin);
            onLogout?.();
            setIsLoggingOut(false);
        });
    };

    const api_Get = async (
        accessPath: string,
        url: string,
        notificationManager: NotificationManagerAPI | null = null,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailAny | null = null,
        onFinally: OnFinally | null = null
    ): Promise<ApiCallResult> => {
        return apiBase("GET", accessPath, url, null, notificationManager, onSuccess, onFail, onFinally);
    };

    const api_Post = async (
        accessPath: string,
        url: string,
        data: any,
        notificationManager: NotificationManagerAPI | null = null,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailAny | null = null,
        onFinally: OnFinally | null = null
    ): Promise<ApiCallResult> => {
        return apiBase("POST", accessPath, url, data, notificationManager, onSuccess, onFail, onFinally);
    };

    const api_Patch = async (
        accessPath: string,
        url: string,
        data: any,
        notificationManager: NotificationManagerAPI | null = null,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailAny | null = null,
        onFinally: OnFinally | null = null
    ): Promise<ApiCallResult> => {
        return apiBase("PATCH", accessPath, url, data, notificationManager, onSuccess, onFail, onFinally);
    };

    const api_Put = async (
        accessPath: string,
        url: string,
        data: any,
        notificationManager: NotificationManagerAPI | null = null,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailAny | null = null,
        onFinally: OnFinally | null = null
    ): Promise<ApiCallResult> => {
        return apiBase("PUT", accessPath, url, data, notificationManager, onSuccess, onFail, onFinally);
    };

    const api_Delete = async (
        accessPath: string,
        url: string,
        notificationManager: NotificationManagerAPI | null = null,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailAny | null = null,
        onFinally: OnFinally | null = null
    ): Promise<ApiCallResult> => {
        return apiBase("DELETE", accessPath, url, null, notificationManager, onSuccess, onFail, onFinally);
    };

    const api_Diagnostics = async (
        accessPath: string,
        error: any,
        onSuccess: OnSuccessEmpty | null = null,
        onFail: OnFailError | null = null,
        onFinally: OnFinallyEmpty | null = null
    ): Promise<ApiCallResult> => {
        return await api_Post(accessPath, `/diagnostics/`, JSON.stringify({ slack_app_key: "portal", content: KarunaCLOUD_slackDiagnosticsPrettifier(error) }), null, onSuccess, onFail, onFinally);
    };

    const api_PatientAlerts = async (
        accessPath: string,
        notificationManager: NotificationManagerAPI,
        onSuccess: OnSuccessAny | null = null,
        onFail: OnFailError | null = null
    ): Promise<ApiCallResult> => {
        return await api_Get(accessPath, `/patient_alerts/`, notificationManager, onSuccess, onFail);
    };

    const api_PatientUsageStats = async (
        accessPath: string,
        params: string,
        notificationManager: NotificationManagerAPI,
        onSuccess: OnSuccessEmpty | null = null,
        onFail: OnFailError | null = null
    ): Promise<ApiCallResult> => {
        return await api_Get(accessPath, `/patient_usage_stats/?${params}`, notificationManager, onSuccess, onFail);
    };

    const api_Version = async (
        accessPath: string,
        onSuccess: OnSuccessMessage | null = null,
        onFail: OnFailError | null = null
    ): Promise<ApiCallResult> => {
        return await api_Get(
            accessPath,
            `/version/`,
            null,
            (response) => {
                onSuccess?.(response.data.version);
            },
            onFail
        );
    };

    const api_Login = async (
        accessPath: string,
        email: string,
        password: string,
        onSuccess: OnSuccessMessage | null,
        onFail: OnFailError | null,
        logDiagnostics: boolean = true
    ): Promise<void> => {
        let logPrefix = `${accessPath ? accessPath : ""}[/login/] `;

        if (isLoggingIn) {
            console.log(`${logPrefix}Rejecting login (Login Already In Progress)`);
            return;
        }

        setIsLoggingIn(true);
        console.log(`${logPrefix}Logging in`);

        revokeAccountAccess();

        const result: ApiCallResult = await api_Post(
            accessPath,
            "/login/",
            {
                email: email,
                password: password
            },
            null,
            (response) => {
                loginResponseValidator(
                    response,
                    async (data) => {
                        grantAccountAccess(data.key, data.user, data.user.role_type, window.location.protocol === "https:");

                        // Initialize session after successful login
                        sessionManager.initialize();

                        // Auto-connect to MCP server using default configuration after successful login
                        try {
                            if (mcpClientService.isEnabled()) {
                                await mcpClientService.connect();
                                console.log(`${accessPath} MCP auto-connection successful after login`);
                            }
                        }
                        catch (mcpError) {
                            console.warn(`${accessPath} MCP auto-connection failed after login:`, mcpError);
                        }

                        onSuccess?.(data);
                        if (logDiagnostics) {
                            void api_Diagnostics(accessPath, { message: `[Portal] Login by ${data.user.first_name} ${data.user.last_name}` });
                        }
                    },
                    (errorMsg) => {
                        onFail?.(errorMsg);
                    }
                );
            },
            (error) => {
                let errorMsg = error.status === 400 ? "Username or password is incorrect" : error.message;
                onFail?.(errorMsg);
            },
            () => {
                setIsLoggingIn(false);
            }
        );

        if (!result.validResponse) {
            loginResponseValidator(result.response, null, onFail);
        }
    };

    const api_Logout = async (
        accessPath: string,
        logoutReason: string,
        logoutHook: LogoutHook | null = null,
        onLogout: OnLogout | null = null,
        routeToLogin: boolean = true,
        logDiagnostics: boolean = true
    ): Promise<void> => {
        const logPrefix = `${accessPath || ""}[/logout/]`;

        const performLogout = async () => {
            if (!isLoggedIn()) {
                console.warn(`${logPrefix} Ignoring logout (Not Logged In): reason[${logoutReason}]`);
                revokeAccountAccess(routeToLogin);
                onLogout?.();
                return;
            }

            if (isLoggingOut) {
                console.warn(`${logPrefix} Ignoring logout (Logout Already In Progress): reason[${logoutReason}]`);
                return;
            }

            setIsLoggingOut(true);

            let user = cookie.load("user");
            let logoutMessage = `[Portal] Logout by ${user ? `${user.first_name} ${user.last_name}` : "UNKNOWN USER"} from ${unhashAccessPath(accessPath)}: reason[${logoutReason}]`;
            console.log(logoutMessage);

            if (logDiagnostics) {
                await api_Diagnostics(
                    accessPath,
                    {
                        message: logoutMessage
                    }
                );
            }

            // Disconnect from MCP server before logout
            try {
                await mcpClientService.disconnect();
                console.log(`${accessPath} MCP disconnected on logout`);
            }
            catch (mcpError) {
                console.warn(`${accessPath} Failed to disconnect from on logout:`, mcpError);
            }

            // Destroy the session before logout
            sessionManager.destroy();

            logout(accessPath, onLogout, routeToLogin);
        };

        if (!logoutHook) {
            await performLogout();
        }
        else {
            await logoutHook(
                accessPath,
                logoutReason,
                () => {
                    console.log(`${accessPath} Logout hook allowed logout`);
                    void performLogout();
                },
                () => {
                    console.log(`${accessPath} Logout hook rejected logout`);
                }
            );
        }
    };

    return {
        isLoggingIn,
        isLoggingOut,
        setIsLoggingIn,
        setIsLoggingOut,
        api_Get,
        api_Post,
        api_Patch,
        api_Put,
        api_Delete,
        api_Diagnostics,
        api_PatientAlerts,
        api_PatientUsageStats,
        api_Version,
        api_Login,
        api_Logout,
        logout
    };
};

const loginResponseValidator = (
    response: any,
    onSuccess: ((data: any) => void) | null = null,
    onFail: ((errorMsg: string) => void) | null = null
) => {

    if (!response) {
        onFail?.("KarunaCLOUD is currently unavailable. Please try again later.");
        return;
    }

    let status = response?.status || null;
    let data = response?.data || null;
    let token = data?.token || null;

    if (status === 400 || status === 401 || !token) {
        onFail?.("Username or password is incorrect.");
        return;
    }

    if (status > 400 || !data) {
        onFail?.("KarunaCLOUD is not responding properly. Please try again later or contact an admin.");
        return;
    }

    onSuccess?.(data);
};

// Export the login response validator for use in other components
export { loginResponseValidator };

// For backward compatibility during the transition, export the default
export default useAPI;
